import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { alpha } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import AppNavbar from "../components/AppNavbar";
import Header from "../components/Header";
import SideMenu from "../components/SideMenu";
import AppTheme from "../shared-theme/AppTheme";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from "../theme/customizations";
import PageErrorBoundary from "../components/PageErrorBoundary";
import { useTranslation } from "react-i18next";
import { getNode } from "../api/nodeAPI";
import { getWebUsers } from "../api/webUserAPI";

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

export default function CreateRoute(props) {
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(0);
  const [nodes, setNodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loadingNodes, setLoadingNodes] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const steps = [
    t("createRoute.selectNodes"),
    t("createRoute.selectUser"),
    t("createRoute.create"),
  ];

  // Fetch nodes on mount
  useEffect(() => {
    const fetchNodes = async () => {
      try {
        setLoadingNodes(true);
        const result = await getNode();
        setNodes(result || []);
      } catch (err) {
        console.error("Error fetching nodes:", err);
      } finally {
        setLoadingNodes(false);
      }
    };
    fetchNodes();
  }, []);

  // Fetch users when moving to step 2
  useEffect(() => {
    if (activeStep === 1 && users.length === 0) {
      const fetchUsers = async () => {
        try {
          setLoadingUsers(true);
          const result = await getWebUsers();
          setUsers(result || []);
        } catch (err) {
          console.error("Error fetching users:", err);
        } finally {
          setLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [activeStep, users.length]);

  const nodeColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: t("createRoute.nodeName"),
        flex: 1,
        minWidth: 200,
      },
      {
        field: "idNode",
        headerName: "ID",
        width: 80,
      },
    ],
    [t],
  );

  const userColumns = useMemo(
    () => [
      {
        field: "username",
        headerName: t("createRoute.username"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "firstName",
        headerName: t("createRoute.firstName"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "lastName",
        headerName: t("createRoute.lastName"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "email",
        headerName: t("createRoute.email"),
        flex: 1,
        minWidth: 200,
      },
    ],
    [t],
  );

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCreateRoute = () => {
    const selectedNodes = nodes.filter((n) =>
      selectedNodeIds.includes(n.idNode),
    );
    const selectedUser = users.find((u) => u.id === selectedUserId);
    console.log("Route created:", { selectedNodes, selectedUser });
    // TODO: API call
  };

  const isNextDisabled =
    (activeStep === 0 && selectedNodeIds.length === 0) ||
    (activeStep === 1 && !selectedUserId);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: "flex" }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: "auto",
          })}
        >
          <PageErrorBoundary>
            <Stack
              spacing={2}
              sx={{
                alignItems: "center",
                mx: 3,
                pb: 5,
                mt: { xs: 8, md: 0 },
              }}
            >
              <Header />

              <Box sx={{ width: "100%", maxWidth: 900 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {/* Step 0: Node Selection */}
                {activeStep === 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t("createRoute.selectNodesTitle")}
                    </Typography>
                    {loadingNodes ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <DataGrid
                        rows={nodes}
                        columns={nodeColumns}
                        getRowId={(row) => row.idNode}
                        checkboxSelection
                        rowSelectionModel={selectedNodeIds}
                        onRowSelectionModelChange={(newSelection) =>
                          setSelectedNodeIds(newSelection)
                        }
                        initialState={{
                          pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                        disableRowSelectionOnClick
                        sx={{ mb: 2 }}
                      />
                    )}
                  </Box>
                )}

                {/* Step 1: User Selection */}
                {activeStep === 1 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t("createRoute.selectUserTitle")}
                    </Typography>
                    {loadingUsers ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <DataGrid
                        rows={users}
                        columns={userColumns}
                        getRowId={(row) => row.id}
                        checkboxSelection
                        rowSelectionModel={
                          selectedUserId ? [selectedUserId] : []
                        }
                        onRowSelectionModelChange={(newSelection) => {
                          const latest =
                            newSelection.length > 0
                              ? newSelection[newSelection.length - 1]
                              : null;
                          setSelectedUserId(latest);
                        }}
                        initialState={{
                          pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                        disableRowSelectionOnClick
                        sx={{ mb: 2 }}
                      />
                    )}
                  </Box>
                )}

                {/* Step 2: Create Route */}
                {activeStep === 2 && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t("createRoute.summary")}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      <strong>{t("createRoute.selectedNodes")}:</strong>{" "}
                      {selectedNodeIds.length} {t("createRoute.nodeCount")}
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      {nodes
                        .filter((n) => selectedNodeIds.includes(n.idNode))
                        .map((n) => n.name)
                        .join(", ")}
                    </Typography>
                    <Typography sx={{ mb: 3 }}>
                      <strong>{t("createRoute.selectedUser")}:</strong>{" "}
                      {users.find((u) => u.id === selectedUserId)?.username ||
                        "-"}
                    </Typography>
                  </Box>
                )}

                {/* Navigation Buttons */}
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                  >
                    {t("createRoute.back")}
                  </Button>

                  {activeStep < 2 ? (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={isNextDisabled}
                    >
                      {t("createRoute.next")}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleCreateRoute}
                      disabled={
                        selectedNodeIds.length === 0 || !selectedUserId
                      }
                    >
                      {t("createRoute.createButton")}
                    </Button>
                  )}
                </Box>
              </Box>
            </Stack>
          </PageErrorBoundary>
        </Box>
      </Box>
    </AppTheme>
  );
}
